"use client";

import { useState } from "react";
import { useServerInsertedHTML } from "next/navigation";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";

/**
 * Registry de Emotion para el App Router de Next.js.
 *
 * Emotion es CSS-in-JS en tiempo de ejecución; para que los estilos generados
 * en el servidor lleguen al HTML inicial (sin parpadeo) hay que inyectarlos
 * mediante `useServerInsertedHTML`. Este provider se usa solo para los
 * elementos con estilos dinámicos: Tailwind 4 + shadcn siguen siendo la base
 * estática de estilos.
 */
export function EmotionRegistry({ children }: { children: React.ReactNode }) {
	const [{ cache, flush }] = useState(() => {
		const cache = createCache({ key: "emotion" });
		cache.compat = true;
		const prevInsert = cache.insert.bind(cache);
		let inserted: string[] = [];
		cache.insert = (...args) => {
			const serialized = args[1];
			if (cache.inserted[serialized.name] === undefined) {
				inserted.push(serialized.name);
			}
			return prevInsert(...args);
		};
		const flush = () => {
			const prev = inserted;
			inserted = [];
			return prev;
		};
		return { cache, flush };
	});

	useServerInsertedHTML(() => {
		const names = flush();
		if (names.length === 0) return null;
		let styles = "";
		for (const name of names) {
			styles += cache.inserted[name];
		}
		return (
			<style
				data-emotion={`${cache.key} ${names.join(" ")}`}
				dangerouslySetInnerHTML={{ __html: styles }}
			/>
		);
	});

	return <CacheProvider value={cache}>{children}</CacheProvider>;
}

export default EmotionRegistry;
